import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ChevronsUpDown, X, Search, Check, Loader2 } from 'lucide-react';

// Async, server-side searchable, infinite-scroll single-select dropdown
// Props:
// - fetcher: ({ page, pageSize, search }) => Promise<{ items: any[], hasNext?: boolean }>
// - getOptionLabel: (item) => string
// - getOptionValue: (item) => string | number
// - value: { label: string, value: string | number } | null
// - onChange: (option: { label: string, value: string | number, item?: any } | null) => void
// - placeholder?: string
// - pageSize?: number (default 10)
export function AsyncSingleSelectDropdown({
  fetcher,
  getOptionLabel,
  getOptionValue,
  value,
  onChange,
  placeholder = 'Select...',
  pageSize = 10,
  loadMoreMode = 'scroll',
  loadMoreLabel = 'Load More',
  searchPlaceholder = 'Search...',
  valueDisplay = 'chip',
  showSelectionIndicator = true,
  showClearButton = true,
  // Optional: (option) => ReactNode — replaces the plain `option.label`
  // text in each dropdown row, for callers that want a richer row (avatar,
  // metadata line, ...). Leaves every other usage of this component
  // (JobPostingsModal.jsx, CreateRequirement.jsx) unaffected when omitted.
  renderOption,
  // Optional: forces a fresh fetchPage(1) every time this dropdown is
  // opened, instead of only on its very first open. Without this, closing
  // and reopening the popup shows the same cached `options` from the
  // previous open (only hasFetchedRef resets on close, not the options
  // array itself) — stale for data that can change between opens, like a
  // candidate's creditable invoices. Defaults to false so every other
  // caller keeps today's cached-across-reopens behavior.
  refetchOnOpen = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]); // Array<{label,value,item}>
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const debouncedSearch = useRef();
  const hasFetchedRef = useRef(false);

  const hasMore = useMemo(() => hasNext, [hasNext]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    // Reset fetch flag when dropdown closes
    if (!isOpen) {
      hasFetchedRef.current = false;
    } else if (refetchOnOpen) {
      // Clear out the previous open's options so the "initial fetch on
      // open" effect below sees options.length === 0 and fires again —
      // hasFetchedRef is already false from the close above.
      setOptions([]);
      setPage(1);
      setHasNext(false);
    }
  }, [isOpen, refetchOnOpen]);

  const fetchPage = useCallback(
    async (requestedPage, replace) => {
      try {
        setIsLoading(true);
        setError(null);
        const { items, hasNext: remoteHasNext } = await fetcher({
          page: requestedPage,
          pageSize,
          search: searchTerm,
        });
        const mapped = (items || []).map((item) => ({
          label: getOptionLabel(item),
          value: getOptionValue(item),
          item,
        }));
        setOptions((prev) => {
          const next = replace ? mapped : [...prev, ...mapped];
          const seen = new Set();
          const deduped = [];
          next.forEach((opt) => {
            const key = String(opt.value);
            if (!seen.has(key)) {
              seen.add(key);
              deduped.push(opt);
            }
          });
          return deduped;
        });
        // Prefer hasNext from API, fallback to heuristic: full page = might have more
        setHasNext(
          remoteHasNext !== undefined
            ? remoteHasNext
            : (items?.length || 0) >= pageSize
        );
        setPage(requestedPage);
      } catch (err) {
        setError(err?.message || 'Failed to load options');
      } finally {
        setIsLoading(false);
      }
    },
    [fetcher, getOptionLabel, getOptionValue, pageSize, searchTerm]
  );

  // Debounce search - only when search term changes while open
  const previousSearchTerm = useRef(searchTerm);
  useEffect(() => {
    if (!isOpen) {
      previousSearchTerm.current = searchTerm;
      return () => {};
    }

    // Only trigger if search term actually changed
    if (previousSearchTerm.current === searchTerm) {
      return () => {};
    }

    previousSearchTerm.current = searchTerm;

    if (debouncedSearch.current) clearTimeout(debouncedSearch.current);
    debouncedSearch.current = setTimeout(() => {
      // Reset and fetch page 1 on new search
      setOptions([]);
      setPage(1);
      setHasNext(false);
      hasFetchedRef.current = false; // Reset fetch flag on new search
      fetchPage(1, true);
    }, 300);
    return () => {
      if (debouncedSearch.current) clearTimeout(debouncedSearch.current);
    };
  }, [isOpen, searchTerm, fetchPage]);

  // Initial fetch on open
  useEffect(() => {
    if (
      isOpen &&
      options.length === 0 &&
      !isLoading &&
      !hasFetchedRef.current
    ) {
      hasFetchedRef.current = true;
      fetchPage(1, true);
    }
  }, [isOpen, options.length, isLoading, fetchPage]);

  const handleScroll = (e) => {
    const el = e.currentTarget;
    const threshold = 0.85;
    const atBottom =
      (el.scrollTop + el.clientHeight) / el.scrollHeight >= threshold;
    if (atBottom && !isLoading && hasMore) {
      fetchPage(page + 1, false);
    }
  };

  const selectedOption = value || null;

  const handleKeyDown = (e, callback) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback();
    }
  };

  const handleSelect = (option) => {
    if (selectedOption && selectedOption.value === option.value) {
      onChange(option);
      setIsOpen(false);
      return;
    }
    onChange(option);
    setIsOpen(false);
  };

  const clearSelection = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(e) => handleKeyDown(e, () => setIsOpen(!isOpen))}
        className="flex items-center justify-between w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground-strong placeholder:text-muted-foreground outline-none focus:border-primary focus:ring-[3px] focus:ring-primary/[0.08] disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <div className="flex items-center gap-2">
          {(() => {
            if (!selectedOption) {
              return (
                <span className="text-muted-foreground">{placeholder}</span>
              );
            }
            if (valueDisplay === 'chip') {
              return (
                <div className="flex items-center bg-secondary text-secondary-foreground rounded px-1 py-0.5 text-xs">
                  {selectedOption.label}
                  {showClearButton && (
                    <button
                      type="button"
                      onClick={clearSelection}
                      className="ml-1 text-secondary-foreground hover:text-primary cursor-pointer"
                      aria-label={`Clear ${selectedOption.label}`}
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              );
            }
            return (
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground">
                  {selectedOption.label}
                </span>
                {showClearButton && (
                  <button
                    type="button"
                    onClick={clearSelection}
                    className="text-muted-foreground hover:text-primary cursor-pointer"
                    aria-label={`Clear ${selectedOption.label}`}
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            );
          })()}
        </div>
        <ChevronsUpDown className="h-4 w-4 opacity-50" />
      </div>

      {isOpen && (
        <div
          className="absolute mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-md z-50"
          role="listbox"
        >
          <div className="flex items-center border-b px-3 py-2">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex h-9 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Search options"
            />
          </div>
          <div
            className="max-h-[300px] overflow-auto"
            onScroll={loadMoreMode === 'scroll' ? handleScroll : undefined}
          >
            {options.map((option) => (
              <div
                key={option.value}
                role="option"
                tabIndex={0}
                aria-selected={selectedOption?.value === option.value}
                onClick={() => handleSelect(option)}
                onKeyDown={(e) => handleKeyDown(e, () => handleSelect(option))}
                className="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
              >
                {showSelectionIndicator && (
                  <div className="mr-2 h-4 w-4 shrink-0 rounded-sm border border-primary flex items-center justify-center">
                    {selectedOption?.value === option.value && (
                      <Check className="h-3 w-3 text-primary" />
                    )}
                  </div>
                )}
                {renderOption ? (
                  renderOption(option)
                ) : (
                  <span>{option.label}</span>
                )}
              </div>
            ))}

            {isLoading && (
              <div className="py-3 text-center text-sm flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading...
              </div>
            )}
            {!isLoading && options.length === 0 && !error && (
              <div className="py-6 text-center text-sm">No results found.</div>
            )}
            {!isLoading && error && (
              <div className="py-6 text-center text-sm text-red-600">
                {error}
              </div>
            )}
            {!isLoading && !hasMore && options.length > 0 && (
              <div className="py-3 text-center text-xs text-muted-foreground">
                End of results
              </div>
            )}
          </div>
          {loadMoreMode === 'button' && hasMore && !isLoading && (
            <div className="border-t px-3 py-2">
              <button
                type="button"
                className="w-full text-xs font-semibold text-primary hover:underline"
                onClick={() => fetchPage(page + 1, false)}
              >
                {loadMoreLabel}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
